import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,

  Paper,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  AccountBalance as BankIcon,
  Assignment as DocumentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Group as GroupIcon,
  GetApp as DownloadIcon,
  MonetizationOn as MoneyIcon,
  Home as HomeIcon,
  Gavel as LegalIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const escrowParticipants = [
  {
    title: 'Buyer',
    role: 'Property Purchaser',
    responsibilities: [
      'Deposit earnest money into escrow',
      'Complete mortgage application and provide documentation',
      'Order and pay for property inspections',
      'Review all closing documents',
      'Provide final walkthrough approval',
      'Bring funds to closing (certified/wire)'
    ],
    timing: 'Throughout transaction',
    icon: HomeIcon,
    color: '#16a34a'
  },
  {
    title: 'Seller',
    role: 'Property Owner',
    responsibilities: [
      'Provide clear title and necessary documentation',
      'Complete required repairs or credits',
      'Maintain property insurance until closing',
      'Vacate property per agreement terms',
      'Sign deed and transfer documents',
      'Pay outstanding liens and mortgages'
    ],
    timing: 'Throughout transaction',
    icon: SecurityIcon,
    color: '#1e40af'
  },
  {
    title: 'Real Estate Agents',
    role: 'Transaction Facilitators',
    responsibilities: [
      'Negotiate contract terms and amendments',
      'Coordinate inspections and repairs',
      'Monitor transaction progress and deadlines',
      'Communicate updates to all parties',
      'Attend final walkthrough and closing',
      'Ensure commission payment from escrow'
    ],
    timing: 'Contract to closing',
    icon: GroupIcon,
    color: '#f59e0b'
  },
  {
    title: 'Lender',
    role: 'Mortgage Provider',
    responsibilities: [
      'Process loan application and underwriting',
      'Order appraisal and title insurance',
      'Provide loan commitment and conditions',
      'Prepare final loan documents',
      'Fund loan at closing',
      'Receive mortgage lien position'
    ],
    timing: 'Application to funding',
    icon: BankIcon,
    color: '#dc2626'
  },
  {
    title: 'Title/Escrow Agent',
    role: 'Neutral Third Party',
    responsibilities: [
      'Hold earnest money and closing funds',
      'Conduct title search and examination',
      'Issue title insurance policies',
      'Prepare closing documents',
      'Coordinate closing meeting',
      'Record deed and distribute funds'
    ],
    timing: 'Contract to recording',
    icon: LegalIcon,
    color: '#7c3aed'
  }
];

const escrowTimeline = [
  {
    day: 'Day 1',
    milestone: 'Contract Acceptance',
    description: 'Executed purchase agreement opens escrow',
    tasks: [
      'Buyer deposits earnest money',
      'Title order placed',
      'Preliminary title report ordered'
    ],
    responsible: 'Buyer/Agent'
  },
  {
    day: 'Days 1-7',
    milestone: 'Initial Setup',
    description: 'All parties begin their respective processes',
    tasks: [
      'Mortgage application submitted',
      'Property inspection scheduled',
      'Title search initiated',
      'Insurance binder ordered'
    ],
    responsible: 'All Parties'
  },
  {
    day: 'Days 7-14',
    milestone: 'Due Diligence',
    description: 'Buyer investigates property condition',
    tasks: [
      'Home inspection completed',
      'Appraisal ordered by lender',
      'Title examination conducted',
      'HOA documents reviewed'
    ],
    responsible: 'Buyer/Lender'
  },
  {
    day: 'Days 14-21',
    milestone: 'Approval & Negotiation',
    description: 'Issues identified and resolved',
    tasks: [
      'Inspection contingencies resolved',
      'Repair negotiations completed',
      'Loan conditions satisfied',
      'Title issues cleared'
    ],
    responsible: 'Buyer/Seller'
  },
  {
    day: 'Days 21-30',
    milestone: 'Final Preparation',
    description: 'Closing preparation and coordination',
    tasks: [
      'Loan underwriting completed',
      'Final title commitment issued',
      'Closing documents prepared',
      'Funding arrangements confirmed'
    ],
    responsible: 'Lender/Title Agent'
  },
  {
    day: 'Days 30-45',
    milestone: 'Closing & Recording',
    description: 'Transaction completion and fund distribution',
    tasks: [
      'Final walkthrough conducted',
      'Closing meeting held',
      'Documents signed and notarized',
      'Funds distributed and deed recorded'
    ],
    responsible: 'Title Agent'
  }
];

const escrowDocuments = [
  {
    category: 'Purchase Documents',
    documents: [
      'Purchase and Sale Agreement',
      'Addenda and Amendments',
      'Disclosure Statements',
      'HOA Documents (if applicable)'
    ]
  },
  {
    category: 'Financial Documents',
    documents: [
      'Earnest Money Receipt',
      'Loan Application and Approval',
      'Closing Disclosure (CD)',
      'Wire Transfer Instructions'
    ]
  },
  {
    category: 'Title Documents',
    documents: [
      'Preliminary Title Report',
      'Title Commitment',
      'Title Insurance Policies',
      'Deed and Transfer Documents'
    ]
  },
  {
    category: 'Property Documents',
    documents: [
      'Property Survey',
      'Inspection Reports',
      'Appraisal Report',
      'Certificate of Occupancy'
    ]
  }
];

const EscrowProcessExplained: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | false>(false);

  const handleSectionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  return (
    <>
      <Helmet>
        <title>Escrow Process Explained | Complete Guide | MAD Title Agency</title>
        <meta
          name="description"
          content="Complete guide to the real estate escrow process in New Jersey. Understanding timelines, responsibilities, and what to expect from contract to closing."
        />
        <meta
          name="keywords"
          content="escrow process, real estate escrow, New Jersey escrow, closing process, title escrow, Middlesex County escrow, escrow agent"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Escrow Process Explained
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#7c3aed', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Consumer Education"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                Escrow Process Explained
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Your complete guide to understanding the escrow process from contract to closing
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#f59e0b',
                  color: '#1f2937',
                  '&:hover': { backgroundColor: '#d97706' },
                }}
              >
                Download Escrow Guide
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <SecurityIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Secure & Protected
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Your funds and documents are safe
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* What is Escrow */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#7c3aed' }}>
            What is Escrow?
          </Typography>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Escrow is a neutral third-party service that holds funds, documents, and other assets during 
                a real estate transaction. The escrow agent acts as an intermediary, ensuring that all 
                conditions of the purchase agreement are met before releasing funds and transferring ownership.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Think of escrow as a "holding account" that protects both buyers and sellers. Neither party 
                can access the funds or complete the transaction until all terms and conditions are satisfied. 
                This provides security and peace of mind throughout the entire process.
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Protection for All:</strong> Escrow ensures that buyers don't lose their money and 
                  sellers don't transfer property without receiving payment.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#fef3ff' }}>
                <SecurityIcon sx={{ fontSize: 48, color: '#7c3aed', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Escrow Benefits
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Neutral third-party protection" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Secure fund management" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Document coordination" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary="Condition verification" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Escrow Participants */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#7c3aed' }}>
            Who's Involved in Escrow?
          </Typography>
          <Grid container spacing={4}>
            {escrowParticipants.map((participant, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', border: `2px solid ${participant.color}20` }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          backgroundColor: `${participant.color}15`,
                          borderRadius: '50%',
                          p: 1.5,
                          mr: 2,
                        }}
                      >
                        <participant.icon sx={{ color: participant.color, fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {participant.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {participant.role}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: participant.color }}>
                      Key Responsibilities:
                    </Typography>
                    <List dense>
                      {participant.responsibilities.map((responsibility, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckIcon sx={{ color: participant.color, fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={responsibility} 
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Active Period:</strong> {participant.timing}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Escrow Timeline */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#7c3aed' }}>
            Escrow Process Timeline
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Here's what happens during a typical 30-45 day escrow period:
          </Typography>
          <Grid container spacing={4}>
            {escrowTimeline.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper sx={{ p: 3, backgroundColor: '#fef3ff', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip 
                      label={item.day} 
                      sx={{ backgroundColor: '#7c3aed', color: 'white', mr: 2 }}
                    />
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                      {item.milestone}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {item.description}
                  </Typography>
                  <List dense>
                    {item.tasks.map((task, taskIndex) => (
                      <ListItem key={taskIndex} sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 25 }}>
                          <CheckIcon sx={{ color: '#16a34a', fontSize: 16 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={task} 
                          primaryTypographyProps={{ variant: 'body2', fontSize: '0.875rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600 }}>
                    Primary Responsibility: {item.responsible}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Documents in Escrow */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#7c3aed' }}>
            Documents Managed in Escrow
          </Typography>
          <Grid container spacing={4}>
            {escrowDocuments.map((category, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <DocumentIcon sx={{ color: '#7c3aed', mr: 2, fontSize: 32 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {category.category}
                      </Typography>
                    </Box>
                    <List>
                      {category.documents.map((document, docIndex) => (
                        <ListItem key={docIndex}>
                          <ListItemIcon>
                            <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={document} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Common Escrow Questions */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#7c3aed' }}>
            Frequently Asked Questions
          </Typography>

          <Accordion
            expanded={expandedSection === 'faq1'}
            onChange={handleSectionChange('faq1')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                What happens to my earnest money in escrow?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your earnest money is deposited into a secure, interest-bearing trust account managed by the 
                escrow agent. These funds are protected and cannot be released without proper authorization 
                from all parties or according to the terms of your purchase agreement.
              </Typography>
              <Alert severity="success">
                <Typography variant="body2">
                  At closing, your earnest money is credited toward your down payment and closing costs.
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSection === 'faq2'}
            onChange={handleSectionChange('faq2')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Who chooses the escrow agent?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ mb: 2 }}>
                In New Jersey, the choice of escrow agent is typically negotiated in the purchase agreement. 
                It may be suggested by the buyer's or seller's real estate agent, or sometimes the lender 
                has a preferred title company. The buyer usually has the right to choose the title/escrow company.
              </Typography>
              <Alert severity="info">
                <Typography variant="body2">
                  Choose an experienced, local escrow agent who understands New Jersey real estate law and procedures.
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSection === 'faq3'}
            onChange={handleSectionChange('faq3')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                What if something goes wrong during escrow?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ mb: 2 }}>
                If issues arise (such as inspection problems, financing issues, or title defects), the escrow 
                agent works with all parties to resolve them. The purchase agreement typically includes 
                contingencies that allow buyers to cancel under certain conditions and receive their earnest money back.
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: '#f59e0b' }} />
                  </ListItemIcon>
                  <ListItemText primary="Communication is key - stay in touch with your escrow agent" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: '#f59e0b' }} />
                  </ListItemIcon>
                  <ListItemText primary="Document everything in writing" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: '#f59e0b' }} />
                  </ListItemIcon>
                  <ListItemText primary="Understand your contingency deadlines" />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedSection === 'faq4'}
            onChange={handleSectionChange('faq4')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                How much does escrow service cost?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Escrow fees are typically split between buyer and seller, though this can be negotiated. 
                In New Jersey, escrow services are often bundled with title insurance services. 
                Total costs usually range from $800-$2,000 depending on the property value and complexity.
              </Typography>
              <Alert severity="info">
                <Typography variant="body2">
                  The peace of mind and protection provided by professional escrow services far outweighs the cost.
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* MAD Title Escrow Services */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#7c3aed' }}>
            MAD Title Escrow Services
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#f0fdf4' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  ✅ Why Choose MAD Title for Escrow
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Local Expertise"
                      secondary="Deep knowledge of Middlesex County procedures and requirements"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Secure Fund Management"
                      secondary="FDIC-insured trust accounts with complete audit trails"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Communication Excellence"
                      secondary="Regular updates and proactive problem-solving"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Technology Integration"
                      secondary="Online tracking and digital document management"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#fef3ff' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  🏠 Our Escrow Services Include
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Earnest Money Management"
                      secondary="Secure deposit and tracking of all buyer funds"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Document Coordination"
                      secondary="Collection, review, and management of all transaction documents"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Condition Verification"
                      secondary="Ensuring all purchase agreement terms are satisfied"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Closing Coordination"
                      secondary="Scheduling and managing the closing process"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Fund Distribution"
                      secondary="Accurate calculation and distribution of all proceeds"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Related Resources */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#7c3aed' }}>
            Related Resources
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <MoneyIcon sx={{ fontSize: 48, color: '#7c3aed', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Closing Cost Calculator
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Estimate your total closing costs including escrow fees.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/closing-cost-calculator"
                  fullWidth
                >
                  Calculate Costs
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <HomeIcon sx={{ fontSize: 48, color: '#7c3aed', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  First-Time Buyer Guide
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Complete guide for first-time homebuyers in Middlesex County.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/first-time-buyer-middlesex"
                  fullWidth
                >
                  Learn More
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <CheckIcon sx={{ fontSize: 48, color: '#7c3aed', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Closing Checklist
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Step-by-step checklist for a smooth closing process.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/middlesex-closing-checklist"
                  fullWidth
                >
                  View Checklist
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#fef3ff', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Ready to Start Your Escrow Process?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Trust MAD Title Agency to handle your escrow with professionalism, security, and expertise. 
              Your peace of mind is our priority.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Start Your Transaction
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="mailto:madtitleagency@gmail.com"
                sx={{
                  borderColor: '#7c3aed',
                  color: '#7c3aed',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Questions About Escrow?
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default EscrowProcessExplained; 